// services/TrackingService.ts
import {
  CartAbandonmentType,
  NavigationEntry,
  OrderTrackingData,
  TrackingData,
} from "../types/tracking";
import { FUNNEL_TRUST_PILOT, type FunnelTypes } from "@/types/funnelTypes";
import type { CartAbandonmentBusiness } from "@/utils/tracking/abandonedCartTracking";
import type { TrackingUpdateResponse } from "@/types/trackingApiTypes";
import {
  GOOGLE_AD_PARAM_NAMES,
  UTM_PARAM_NAMES,
  persistNurturingAttribution,
  clearPersistedFirstTouchAttribution,
  extractCurrentAttributionFromUrl,
  getOrCreatePersistedSession,
  getPersistedNurturingAttribution,
  omitStorageMetadata,
  persistFirstTouchAttribution,
  sanitizeUrlToAttributionParams,
  stripAllAttributionParamsFromUrl,
} from "@/utils/analytics/attribution";
import {
  getCookiebotConsentSnapshot,
  getCookiebotConsentState,
  hasFirstTouchStorageConsent,
  hasFirstTouchStorageWithdrawal,
} from "@/utils/analytics/cookiebot";
import {
  createGa4IdentifierCache,
  type Ga4IdentifierCache,
  type Ga4Identifiers,
} from "@/utils/analytics/ga4Identifiers";
import { redactMarketingTrackingPayload } from "@/utils/analytics/trackingConsent";
import { reportToFlare } from "@/utils/reportToFlare";
import { resolveApiProofUrl } from "@/utils/tracking/resolveApiProofUrl";
import {
  OrderTrackingAlreadySentError,
  hasSentOrderTrackingUrl,
  markOrderTrackingUrlAsSent,
  unmarkOrderTrackingUrlAsSent,
} from "@/utils/tracking/orderTrackingDedupe";
import { safelyReportToFlare } from "@/utils/monitoring/safelyReportToFlare";
import {logSafeError} from "@/utils/logSafeError";
import {REPUTECH_DOMAIN} from "@/utils/constants/brand";

const KEEPALIVE_MAX_BODY_BYTES = 60 * 1024;
const KEEPALIVE_HISTORY_LIMIT = 20;
const COOKIEBOT_CONSENT_EVENTS = [
  "CookiebotOnLoad",
  "CookiebotOnAccept",
  "CookiebotOnConsentChanged",
  "CookiebotOnDecline",
] as const;
const isBrowserFetchNetworkError = (error: unknown): boolean =>
  error instanceof TypeError &&
  /^(failed to fetch|load failed|networkerror when attempting to fetch resource\.?|fetch failed)$/i.test(
    error.message.trim(),
  );
const MARKETING_ATTRIBUTION_FIELDS = [
  ...GOOGLE_AD_PARAM_NAMES,
  ...UTM_PARAM_NAMES,
  ...UTM_PARAM_NAMES.map((name) => `first_${name}`),
] as const;

const payloadTextEncoder =
  typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
const KEEPALIVE_COMPACT_FIELDS = [
  "browser_name",
  "browser_version",
  "checkout_duration",
  "dclid",
  "device_type",
  "exit_page",
  "first_landing_page",
  "first_referrer_url",
  "first_utm_campaign",
  "first_utm_content",
  "first_utm_medium",
  "first_utm_source",
  "first_utm_term",
  "frontend_domain",
  "gad_campaignid",
  "gad_source",
  "ga_client_id",
  "ga_session_id",
  "ga_session_observed_at",
  "cookiebot_has_response",
  "cookiebot_consented",
  "cookiebot_declined",
  "cookiebot_consent_necessary",
  "cookiebot_consent_preferences",
  "cookiebot_consent_statistics",
  "cookiebot_consent_marketing",
  "gbraid",
  "gclid",
  "gclsrc",
  "landing_page",
  "language",
  "operating_system",
  "page_views",
  "platform",
  "referrer_url",
  "screen_resolution",
  "session_duration",
  "session_id",
  "timezone",
  "utm_campaign",
  "utm_content",
  "utm_medium",
  "utm_source",
  "utm_term",
  "wbraid",
] as const satisfies readonly (keyof TrackingData)[];

export class TrackingService {
  private sessionStartTime: number;
  private checkoutStartTime: number | null;
  // Distinguishes repeat updates from a genuine switch to another funnel.
  private checkoutFunnelType: FunnelTypes | null;
  private pageViews: number;
  private sessionId: string;
  private navigationPath: NavigationEntry[];
  private referrerChain: string[];
  private filterSelections: Record<string, string[]>;
  private initialLandingPageUrl: string | null;
  private initialReferrerUrl: string | null;
  private isFirstTouchConsentWithdrawn: boolean = false;
  public data: TrackingData;
  private isReady: boolean = false;
  private initPromise: Promise<void> | null = null;
  private ga4IdentifierCache: Ga4IdentifierCache;
  // Signed update URLs are deliberately request-local. A reload starts a new
  // tracking session instead of persisting a bearer-style proof in storage.
  private trackingUpdateUrl: string | null = null;
  private consentListenerTarget: {
    addEventListener: Window["addEventListener"];
    removeEventListener: Window["removeEventListener"];
  } | null;

  constructor() {
    if (typeof window !== "undefined") {
      persistNurturingAttribution({ url: window.location.href });
    }
    const persistedSession = getOrCreatePersistedSession();
    this.sessionStartTime = persistedSession.created_at;
    this.checkoutStartTime = null;
    this.checkoutFunnelType = null;
    this.pageViews = 0;
    // First-party 24h app-session id used to correlate same-session events;
    // storage-denied browsers fall back to an in-memory id inside the helper.
    this.sessionId = persistedSession.session_id;
    this.navigationPath = [];
    this.referrerChain = [];
    this.filterSelections = {};
    this.initialLandingPageUrl =
      typeof window !== "undefined" ? window.location.href : null;
    this.initialReferrerUrl =
      typeof document !== "undefined" && document.referrer
        ? document.referrer
        : null;

    // Initialize data with safe defaults
    this.data = this.createDefaultTrackingData();
    this.ga4IdentifierCache = createGa4IdentifierCache({
      onChange: (identifiers) => this.applyGa4Identifiers(identifiers),
    });
    this.ga4IdentifierCache.start();

    this.consentListenerTarget =
      typeof window === "undefined" ||
      typeof window.addEventListener !== "function"
        ? null
        : window;
    COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
      this.consentListenerTarget?.addEventListener(
        eventName,
        this.consentChangeListener,
      );
    });

    this.waitUntilReady();
  }

  // Initialize immediately but don't block construction
  // The async data will be populated when available
  public async waitUntilReady(): Promise<void> {
    if (this.isReady) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.collectInitialData()
      .catch((error) => {
        logSafeError("tracking-initial-data", error);
        if (typeof window !== "undefined" && window.flare) {
          safelyReportToFlare(window.flare, error as Error, {
            context: "TrackingService initialization",
          });
        }
      })
      .finally(() => {
        this.isReady = true;
      });

    return this.initPromise;
  }

  private createDefaultTrackingData(): TrackingData {
    return {
      frontend_domain: REPUTECH_DOMAIN,
      device_type: "desktop",
      device_details: {
        width: 0,
        height: 0,
        pixelRatio: 1,
      },
      screen_resolution: "0x0",
      geolocation: {},
      operating_system: "Unknown",
      browser_name: "Unknown",
      browser_version: "Unknown",
      user_agent: "",
      timezone: "UTC",
      language: "en",
      referrer_url: "",
      referrer_chain: [],
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
      gclid: null,
      gbraid: null,
      wbraid: null,
      gad_source: null,
      gad_campaignid: null,
      gclsrc: null,
      dclid: null,
      ga_client_id: null,
      ga_session_id: null,
      ga_session_observed_at: null,
      cookiebot_has_response: null,
      cookiebot_consented: null,
      cookiebot_declined: null,
      cookiebot_consent_necessary: null,
      cookiebot_consent_preferences: null,
      cookiebot_consent_statistics: null,
      cookiebot_consent_marketing: null,
      first_landing_page: null,
      first_referrer_url: null,
      first_utm_source: null,
      first_utm_medium: null,
      first_utm_campaign: null,
      first_utm_term: null,
      first_utm_content: null,
      landing_page: "",
      session_id: this.sessionId,
      page_views: 0,
      navigation_path: [],
      platform: "trust-pilot",
    };
  }

  private getOperatingSystem(): string {
    if (typeof window === "undefined") return "Unknown";

    const userAgent = navigator.userAgent;
    let os = "Unknown";

    // Check for mobile OSes first (more specific)
    if (userAgent.indexOf("Android") !== -1) os = "Android";
    else if (userAgent.indexOf("like Mac") !== -1) os = "iOS";
    // Then desktop OSes
    else if (userAgent.indexOf("Win") !== -1) os = "Windows";
    else if (userAgent.indexOf("Mac") !== -1) os = "MacOS";
    else if (userAgent.indexOf("Linux") !== -1) os = "Linux";

    return os;
  }

  private async collectInitialData(): Promise<void> {
    if (typeof window === "undefined") return;

    this.pageViews++;

    this.navigationPath.push({
      url: window.location.href,
      timestamp: Date.now(),
      title: document.title,
    });

    if (document.referrer) {
      this.referrerChain.push(document.referrer);
    }

    const userAgent = navigator.userAgent;
    let deviceType: TrackingData["device_type"] = "desktop";
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      deviceType = "tablet";
    } else if (
      /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile/.test(userAgent)
    ) {
      deviceType = "mobile";
    }

    let browserName = "";
    let browserVersion = "";

    if (userAgent.indexOf("Edge") > -1) {
      browserName = "Edge";
      const match = userAgent.match(/Edge\/([0-9.]+)/);
      browserVersion = match ? match[1] : "";
    } else if (userAgent.indexOf("Firefox") > -1) {
      browserName = "Firefox";
      const match = userAgent.match(/Firefox\/([0-9.]+)/);
      browserVersion = match ? match[1] : "";
    } else if (userAgent.indexOf("Chrome") > -1) {
      browserName = "Chrome";
      const match = userAgent.match(/Chrome\/([0-9.]+)/);
      browserVersion = match ? match[1] : "";
    } else if (userAgent.indexOf("Safari") > -1) {
      browserName = "Safari";
      const match = userAgent.match(/Version\/([0-9.]+)/);
      browserVersion = match ? match[1] : "";
    }

    const firstTouchAttribution = this.captureFirstTouchAttribution(
      this.initialLandingPageUrl ?? window.location.href,
      this.initialReferrerUrl ?? undefined,
    );
    const explicitAttributionData = this.buildExplicitAttributionData(
      window.location.href,
      firstTouchAttribution,
    );
    const cookiebotConsentSnapshot = getCookiebotConsentSnapshot();

    // Attribution is available synchronously for fast exits and later consent
    // transitions. Public payloads still pass through the consent redactor.
    this.data = {
      ...this.data,
      referrer_url: (this.initialReferrerUrl ?? document.referrer) || "",
      referrer_chain: this.referrerChain,
      ...cookiebotConsentSnapshot,
      ...explicitAttributionData,
      landing_page: window.location.href,
      session_id: this.sessionId,
      page_views: this.pageViews,
      navigation_path: this.navigationPath,
    };

    const geolocation: Record<string, any> = {};

    // Update browser enrichment without replacing attribution/consent applied
    // above.
    this.data = {
      ...this.data,
      device_type: deviceType,
      device_details: {
        width: window.innerWidth,
        height: window.innerHeight,
        pixelRatio: window.devicePixelRatio || 1,
      },
      geolocation,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      operating_system: this.getOperatingSystem(),
      browser_name: browserName,
      browser_version: browserVersion,
      user_agent: userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language || "en",
    };

    void this.refreshGa4Identifiers();
  }

  public startCheckoutStartTime(funnelType: FunnelTypes = FUNNEL_TRUST_PILOT) {
    if (
      this.checkoutStartTime !== null &&
      this.checkoutFunnelType === funnelType
    ) {
      return;
    }

    this.restartCheckoutStartTime(funnelType);
  }

  public restartCheckoutStartTime(funnelType: FunnelTypes = FUNNEL_TRUST_PILOT) {
    this.checkoutStartTime = Date.now();
    this.checkoutFunnelType = funnelType;
    void this.refreshGa4Identifiers();
  }

  public finishCheckout() {
    this.checkoutStartTime = null;
    this.checkoutFunnelType = null;
  }

  public recordNavigation(): void {
    if (typeof window === "undefined") return;

    this.pageViews++;
    this.navigationPath.push({
      url: window.location.href,
      timestamp: Date.now(),
      title: document.title,
    });
    this.data.page_views = this.pageViews;
    this.data.navigation_path = this.navigationPath;
  }

  public getSessionDuration(): number {
    return Math.floor((Date.now() - this.sessionStartTime) / 1000);
  }

  public setFilterSelections(
    filterSelection: string,
    businessId: string,
  ): void {
    const data: string[] = this.filterSelections[businessId] ?? [];
    data.push(filterSelection);

    this.filterSelections[businessId] = data;
  }

  public removeFilterSelection(filter: string, businessId: string): void {
    const data: string[] = this.filterSelections[businessId] ?? [];

    this.filterSelections[businessId] = data.filter(
      (selection) => selection !== filter,
    );
  }

  public getCheckoutDuration(): number | null {
    if (this.checkoutStartTime === null) {
      return null;
    }
    return Math.floor((Date.now() - this.checkoutStartTime) / 1000);
  }

  public recordFormAbandonment(
    formId: string,
    formData: Record<string, any>,
  ): void {
    if (!this.data.form_abandonment) {
      this.data.form_abandonment = [];
    }

    this.data.form_abandonment = [
      {
        form_id: formId,
        timestamp: Date.now(),
        data: formData || {},
      },
    ];
  }

  public recordSearchQuery(query: string): void {
    if (!this.data.search_queries) {
      this.data.search_queries = [];
    }

    this.data.search_queries.push({
      query,
      timestamp: Date.now(),
    });
  }

  private getAuthToken(): string {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("auth_token") || "";
  }

  public buildCartAbandonmentData(
    selectedBusiness: CartAbandonmentBusiness[],
    coupon?: string,
  ): CartAbandonmentType {
    let data: CartAbandonmentType = {
      selected_business: selectedBusiness,
      coupon: coupon,
    };

    this.data.cart_abandonment = data;

    return data;
  }

  public buildFilterData(selectedFilters?: {}): void {
    this.data.filter_selections = selectedFilters;
  }

  public buildPlatformData(platform: FunnelTypes): void {
    this.data.platform = platform;
  }

  private buildExplicitAttributionData(
    currentUrl: string,
    persistedFirstTouch: ReturnType<typeof persistFirstTouchAttribution>,
  ): Partial<TrackingData> {
    const currentAttribution = extractCurrentAttributionFromUrl(currentUrl);
    const firstTouch = omitStorageMetadata(persistedFirstTouch);
    // Non-first attribution fields intentionally start from persisted
    // last-known values; URL params on the current page override them.
    // Explicit null defaults keep absent first-touch keys present in JSON.
    const attributionData: Partial<TrackingData> = {
      ...firstTouch,
      first_landing_page: firstTouch.first_landing_page ?? null,
      first_referrer_url: firstTouch.first_referrer_url ?? null,
      first_utm_source: firstTouch.first_utm_source ?? null,
      first_utm_medium: firstTouch.first_utm_medium ?? null,
      first_utm_campaign: firstTouch.first_utm_campaign ?? null,
      first_utm_term: firstTouch.first_utm_term ?? null,
      first_utm_content: firstTouch.first_utm_content ?? null,
    };

    [...GOOGLE_AD_PARAM_NAMES, ...UTM_PARAM_NAMES].forEach((paramName) => {
      attributionData[paramName] = firstTouch[paramName] ?? null;

      const currentValue = currentAttribution[paramName];
      if (currentValue) {
        attributionData[paramName] = currentValue;
      }
    });

    return attributionData;
  }

  private applyGa4Identifiers(identifiers: Ga4Identifiers | null): void {
    this.data.ga_client_id = identifiers?.ga_client_id ?? null;
    this.data.ga_session_id = identifiers?.ga_session_id ?? null;
    this.data.ga_session_observed_at =
      identifiers?.ga_session_observed_at ?? null;
  }

  private refreshGa4Identifiers = async (): Promise<void> => {
    await this.ga4IdentifierCache.refresh();
  };

  private hasFirstTouchStoragePermission(): boolean {
    return hasFirstTouchStorageConsent(getCookiebotConsentSnapshot());
  }

  private captureFirstTouchAttribution(
    url: string,
    referrerUrl?: string,
  ): ReturnType<typeof persistFirstTouchAttribution> {
    if (
      this.isFirstTouchConsentWithdrawn ||
      hasFirstTouchStorageWithdrawal(getCookiebotConsentSnapshot())
    ) {
      this.isFirstTouchConsentWithdrawn = true;
      clearPersistedFirstTouchAttribution();
      return { created_at: 0, expires_at: 0 } as ReturnType<
        typeof persistFirstTouchAttribution
      >;
    }

    return persistFirstTouchAttribution({
      url,
      referrerUrl,
      allowGoogleAdParams: true,
      persistToStorage: this.hasFirstTouchStoragePermission(),
      preserveRawUrls: true,
    });
  }

  private handleCookiebotConsentChange(): void {
    if (typeof window === "undefined") return;

    const consentSnapshot = getCookiebotConsentSnapshot();
    if (hasFirstTouchStorageWithdrawal(consentSnapshot)) {
      this.isFirstTouchConsentWithdrawn = true;
      clearPersistedFirstTouchAttribution();
      this.clearLiveAttributionData();
      return;
    }

    if (hasFirstTouchStorageConsent(consentSnapshot)) {
      this.isFirstTouchConsentWithdrawn = false;
    }

    this.refreshAttributionData();
  }

  private readonly consentChangeListener: () => void = () =>
    this.handleCookiebotConsentChange();

  private refreshAttributionData(): void {
    if (typeof window === "undefined") return;

    const cookiebotConsentSnapshot = getCookiebotConsentSnapshot();

    if (this.isFirstTouchConsentWithdrawn) {
      this.data = {
        ...this.data,
        landing_page: stripAllAttributionParamsFromUrl(window.location.href),
        referrer_url: stripAllAttributionParamsFromUrl(
          (this.initialReferrerUrl ?? document.referrer) || "",
        ),
        ...cookiebotConsentSnapshot,
      };
      return;
    }

    const firstTouchAttribution = this.captureFirstTouchAttribution(
      this.initialLandingPageUrl ?? window.location.href,
      (this.initialReferrerUrl ?? document.referrer) || undefined,
    );

    this.data = {
      ...this.data,
      landing_page: window.location.href,
      referrer_url: (this.initialReferrerUrl ?? document.referrer) || "",
      ...cookiebotConsentSnapshot,
      ...this.buildExplicitAttributionData(
        window.location.href,
        firstTouchAttribution,
      ),
    };
  }

  private clearLiveAttributionData(): void {
    this.data = {
      ...this.data,
      landing_page: stripAllAttributionParamsFromUrl(
        this.data.landing_page || window.location.href,
      ),
      referrer_url: stripAllAttributionParamsFromUrl(this.data.referrer_url),
      ...Object.fromEntries(
        MARKETING_ATTRIBUTION_FIELDS.map((fieldName) => [fieldName, null]),
      ),
      first_landing_page: null,
      first_referrer_url: null,
    };
  }

  private redactTrackingDataWithoutConsent(
    trackingData: TrackingData,
  ): TrackingData {
    const trackingDataSnapshot = this.cloneTrackingDataSnapshot(trackingData);
    const sanitizeUrlField = (url: string): string =>
      this.isFirstTouchConsentWithdrawn
        ? stripAllAttributionParamsFromUrl(url)
        : sanitizeUrlToAttributionParams(url, true);

    trackingDataSnapshot.landing_page = sanitizeUrlField(
      trackingDataSnapshot.landing_page,
    );
    trackingDataSnapshot.referrer_url = sanitizeUrlField(
      trackingDataSnapshot.referrer_url,
    );
    trackingDataSnapshot.exit_page = sanitizeUrlField(
      trackingDataSnapshot.exit_page ?? "",
    );
    trackingDataSnapshot.navigation_path =
      trackingDataSnapshot.navigation_path.map((entry) => ({
        ...entry,
        url: sanitizeUrlField(entry.url),
      }));
    trackingDataSnapshot.referrer_chain =
      trackingDataSnapshot.referrer_chain.map(sanitizeUrlField).filter(Boolean);

    const currentConsentSnapshot = getCookiebotConsentSnapshot();
    Object.assign(trackingDataSnapshot, currentConsentSnapshot);

    const redactedData = redactMarketingTrackingPayload(
      trackingDataSnapshot,
      getCookiebotConsentState("marketing"),
      MARKETING_ATTRIBUTION_FIELDS,
    );

    if (getCookiebotConsentState("analytics") !== "granted") {
      redactedData.ga_client_id = null;
      redactedData.ga_session_id = null;
      redactedData.ga_session_observed_at = null;
    }

    return redactedData;
  }

  public hasCompletedInitialData(): boolean {
    return this.isReady;
  }

  /**
   * @internal Test/manual singleton reset hook. Production keeps the service
   * alive for the page lifetime, so this only clears the shared instance.
   */
  public destroy(): void {
    this.ga4IdentifierCache.destroy();
    COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
      this.consentListenerTarget?.removeEventListener(
        eventName,
        this.consentChangeListener,
      );
    });
    if (_trackingService === this) {
      _trackingService = null;
    }
  }

  private cloneTrackingDataSnapshot(trackingData: TrackingData): TrackingData {
    const filterSelections = trackingData.filter_selections;
    const cartAbandonment = trackingData.cart_abandonment;

    return {
      ...trackingData,
      device_details: { ...trackingData.device_details },
      geolocation: trackingData.geolocation
        ? { ...trackingData.geolocation }
        : trackingData.geolocation,
      referrer_chain: [...trackingData.referrer_chain],
      navigation_path: trackingData.navigation_path.map((entry) => ({
        ...entry,
      })),
      filter_selections: Array.isArray(filterSelections)
        ? [...filterSelections]
        : filterSelections && typeof filterSelections === "object"
          ? { ...filterSelections }
          : filterSelections,
      form_abandonment: trackingData.form_abandonment?.map((entry) => ({
        ...entry,
        data: { ...entry.data },
      })),
      search_queries: trackingData.search_queries?.map((entry) => ({
        ...entry,
      })),
      cart_abandonment:
        cartAbandonment && typeof cartAbandonment === "object"
          ? { ...cartAbandonment }
          : cartAbandonment,
    };
  }

  public getTrackingDataSnapshot(): TrackingData {
    return this.redactTrackingDataWithoutConsent(
      this.cloneTrackingDataSnapshot(this.data),
    );
  }

  public prepareTrackingDataForSend({
    rotateExpiredSession = true,
  }: {
    rotateExpiredSession?: boolean;
  } = {}): TrackingData {
    // This method intentionally mutates live tracking state before returning a
    // snapshot; callers should serialize the result immediately.
    this.ga4IdentifierCache.getFreshIdentifiers();

    // Re-read the fixed-window persisted session so long-lived tabs rotate
    // with the 24h app-session TTL at send time.
    if (rotateExpiredSession) {
      const nextSession = getOrCreatePersistedSession();
      if (nextSession.session_id !== this.sessionId) {
        this.sessionId = nextSession.session_id;
        this.sessionStartTime = nextSession.created_at;
        // The rotation happens during an active page lifetime, so seed the new
        // fixed-window session with the current page instead of sending an empty
        // first payload for the new session id.
        this.pageViews = typeof window !== "undefined" ? 1 : 0;
        this.navigationPath =
          typeof window !== "undefined"
            ? [
                {
                  url: window.location.href,
                  timestamp: Date.now(),
                  title: typeof document !== "undefined" ? document.title : "",
                },
              ]
            : [];
        this.referrerChain = [];
        this.data.page_views = this.pageViews;
        this.data.navigation_path = this.navigationPath;
        this.data.referrer_chain = this.referrerChain;
      }
    }
    this.data.session_id = this.sessionId;
    this.data.session_duration = this.getSessionDuration();
    this.data.checkout_duration = this.getCheckoutDuration();
    this.refreshAttributionData();
    this.data.exit_page =
      typeof window !== "undefined" ? window.location.href : "";

    return this.redactTrackingDataWithoutConsent(
      this.cloneTrackingDataSnapshot(this.data),
    );
  }

  private getPayloadByteSize(payload: string): number {
    if (payloadTextEncoder) {
      return payloadTextEncoder.encode(payload).length;
    }

    return payload.length;
  }

  private buildCompactKeepalivePayload(
    trackingData: TrackingData,
  ): Partial<TrackingData> {
    const compactPayload: Partial<TrackingData> = {};
    KEEPALIVE_COMPACT_FIELDS.forEach((fieldName) => {
      (compactPayload as Record<string, unknown>)[fieldName] =
        trackingData[fieldName];
    });

    return compactPayload;
  }

  private serializeTrackingDataForSend(
    trackingData: TrackingData,
    keepalive: boolean,
  ): string {
    const body = JSON.stringify(trackingData);
    if (
      !keepalive ||
      this.getPayloadByteSize(body) <= KEEPALIVE_MAX_BODY_BYTES
    ) {
      return body;
    }

    const trimmedPayload: TrackingData = {
      ...trackingData,
      navigation_path: trackingData.navigation_path.slice(
        -KEEPALIVE_HISTORY_LIMIT,
      ),
      referrer_chain: trackingData.referrer_chain.slice(
        -KEEPALIVE_HISTORY_LIMIT,
      ),
      search_queries: trackingData.search_queries?.slice(
        -KEEPALIVE_HISTORY_LIMIT,
      ),
      cart_abandonment: null,
    };

    const trimmedBody = JSON.stringify(trimmedPayload);
    if (this.getPayloadByteSize(trimmedBody) <= KEEPALIVE_MAX_BODY_BYTES) {
      return trimmedBody;
    }

    const minimalPayload = {
      ...trimmedPayload,
      form_abandonment: undefined,
      navigation_path: [],
      referrer_chain: [],
      search_queries: undefined,
    };
    const minimalBody = JSON.stringify(minimalPayload);
    if (this.getPayloadByteSize(minimalBody) <= KEEPALIVE_MAX_BODY_BYTES) {
      return minimalBody;
    }

    const compactPayload = this.buildCompactKeepalivePayload(trackingData);
    const compactBody = JSON.stringify(compactPayload);
    if (this.getPayloadByteSize(compactBody) <= KEEPALIVE_MAX_BODY_BYTES) {
      return compactBody;
    }

    const fallbackPayload = {
      landing_page: trackingData.landing_page,
      platform: trackingData.platform,
      session_duration: trackingData.session_duration,
      session_id: trackingData.session_id,
    };
    const fallbackBody = JSON.stringify(fallbackPayload);
    if (this.getPayloadByteSize(fallbackBody) <= KEEPALIVE_MAX_BODY_BYTES) {
      return fallbackBody;
    }

    const finalFallbackBody = JSON.stringify({
      platform: trackingData.platform,
      session_duration: trackingData.session_duration,
      session_id: trackingData.session_id,
    });

    if (
      this.getPayloadByteSize(finalFallbackBody) > KEEPALIVE_MAX_BODY_BYTES &&
      typeof window !== "undefined" &&
      window.flare
    ) {
      safelyReportToFlare(
        window.flare,
        new Error("Tracking keepalive payload exceeds size cap"),
        {
          context: "TrackingService serializeTrackingDataForSend",
        },
      );
      return "{}";
    }

    return finalFallbackBody;
  }

  public async sendTrackingData({
    waitUntilReady = true,
    keepalive = false,
    rotateExpiredSession = true,
  }: {
    waitUntilReady?: boolean;
    keepalive?: boolean;
    rotateExpiredSession?: boolean;
  } = {}): Promise<TrackingUpdateResponse> {
    if (waitUntilReady) {
      await this.waitUntilReady();
    }

    if (keepalive) {
      this.ga4IdentifierCache.getFreshIdentifiers();
    } else {
      await this.refreshGa4Identifiers();
    }

    const trackingData = this.prepareTrackingDataForSend({
      rotateExpiredSession,
    });
    const requestBody = this.serializeTrackingDataForSend(
      trackingData,
      keepalive,
    );

    let responseStatus: number | null = null;

    try {
      const isTrackingUpdate = this.trackingUpdateUrl !== null;
      const requestUrl = resolveApiProofUrl(
        this.trackingUpdateUrl ?? "tracking",
      );
      const response = await fetch(requestUrl, {
        method: isTrackingUpdate ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        keepalive,
        body: requestBody,
      });
      responseStatus = response.status;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = (await response.json()) as TrackingUpdateResponse;

      if (!isTrackingUpdate && typeof responseData.update_url === "string") {
        this.trackingUpdateUrl = responseData.update_url;
      }

      return responseData;
    } catch (error) {
      // Keepalive sends only run while the page is closing. Browsers often
      // drop them there and reject with a network TypeError ("Failed to
      // fetch" / "Load failed") before any response arrives. That is not
      // actionable, so only report failures that reached the server or were
      // not network-level.
      const isDroppedUnloadRequest =
        keepalive && responseStatus === null && error instanceof TypeError;

      if (!isDroppedUnloadRequest) {
        reportToFlare(error, "TrackingService sendTrackingData", {
          responseStatus,
        });
      }
      throw error;
    }
  }

  public async sendOrderTrackingData(
    orderTrackingUrl: string,
    funnelType: FunnelTypes = FUNNEL_TRUST_PILOT,
  ): Promise<TrackingUpdateResponse> {
    if (hasSentOrderTrackingUrl(orderTrackingUrl)) {
      throw new OrderTrackingAlreadySentError();
    }

    markOrderTrackingUrlAsSent(orderTrackingUrl);
    let receivedOkResponse = false;
    let fetchFailedWithNetworkError = false;
    let responseStatus: number | null = null;
    try {
      // Everything after the claim is inside this try so readiness, cookie and
      // serialization failures release it for a legitimate retry.
      await this.waitUntilReady();

      this.ga4IdentifierCache.getFreshIdentifiers();
      void this.refreshGa4Identifiers();

      this.data.filter_selections = this.filterSelections;
      this.data.platform = funnelType;
      delete this.data.form_abandonment;

      const trackingData = this.prepareTrackingDataForSend({
        rotateExpiredSession: false,
      });
      const nurturingAttribution = getPersistedNurturingAttribution();
      const orderTrackingData: OrderTrackingData = nurturingAttribution
        ? { ...trackingData, rh_nurturing: nurturingAttribution.token }
        : trackingData;
      let response: Response;
      try {
        response = await fetch(resolveApiProofUrl(orderTrackingUrl), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(orderTrackingData),
        });
      } catch (error) {
        fetchFailedWithNetworkError = isBrowserFetchNetworkError(error);
        throw error;
      }
      responseStatus = response.status;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      receivedOkResponse = true;
      const responseData = (await response.json()) as TrackingUpdateResponse;

      if (typeof responseData.update_url === "string") {
        this.trackingUpdateUrl = responseData.update_url;
      }

      return responseData;
    } catch (error) {
      if (error instanceof OrderTrackingAlreadySentError) {
        throw error;
      }

      if (!receivedOkResponse) {
        unmarkOrderTrackingUrlAsSent(orderTrackingUrl);
      }
      if (!fetchFailedWithNetworkError) {
        reportToFlare(error, "TrackingService sendOrderTrackingData", {
          funnelType,
          responseStatus,
        });
      }
      throw error;
    }
  }

  public async updateCheckoutData(
    updateUrl: string,
    checkoutData: Record<string, any>,
  ): Promise<TrackingUpdateResponse> {
    let responseStatus: number | null = null;

    try {
      const response = await fetch(resolveApiProofUrl(updateUrl), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(checkoutData),
      });
      responseStatus = response.status;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as TrackingUpdateResponse;
    } catch (error) {
      reportToFlare(error, "TrackingService updateCheckoutData", {
        responseStatus,
      });
      throw error;
    }
  }
}

// Create a singleton instance with lazy initialization
let _trackingService: TrackingService | null = null;

export const getTrackingService = (): TrackingService | null => {
  if (typeof window !== "undefined" && !_trackingService) {
    try {
      _trackingService = new TrackingService();
    } catch (error) {
      logSafeError("tracking-service-init", error);
    }
  }
  return _trackingService;
};

// For backward compatibility
export const trackingService = getTrackingService();

export default TrackingService;
